import React, { useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Container,
  Grid,
  Button,
  Divider,
  makeStyles,
  useTheme
} from '@material-ui/core'
import {
  Controller,
  FormProvider,
  useForm
} from 'react-hook-form'
import { useOktaAuth } from '@okta/okta-react'

import { useAuthState, useBoolean } from '../../../hooks'
import {
  fetchUser,
  updateMe,
  getRecoveryQuestion
} from '../../../service'

import Text from '../../atoms/Text'
import RoundedButton from '../../atoms/RoundedButton'
import SnackAlert from '../../molecules/SnackAlert/SnackAlert'
import ResetPasswordDialog from '../../organisms/ResetPasswordDialog'
import ResetRecoveryQnDialog from '../../organisms/ResetRecoveryQnDialog'
import Select from '../../molecules/Select'

import {
  BUTTON_SIZES,
  ERROR_ALERT,
  SUCCESS_ALERT
} from '../../../constants'

import TextInput from '../../molecules/TextInput'
import ClientProfilePicture from '../admin/ClientDetailsView/ClientProfilePicture'
import { messageToUse } from '../admin/common'
import PreferencesSkeleton from './skeleton'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '1.5rem',
    height: '100%'
  },
  confirmError: {
    textAlign: 'left',
    marginTop: '5px'
  },
  bottomContainer: {
    position: 'absolute',
    bottom: '5%',
    right: 0,
    left: 0,
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      flexDirection: 'column-reverse'
    }
  },
  editBtn: {
    border: `2px solid ${theme.palette.mirage}`,
    color: theme.palette.mirage,
    borderRadius: 25,
    padding: '4px 16px 3px 16px',
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '0.75rem',
    marginTop: '10px'
  },
  infoContainer: {
    marginBottom: '2.5rem'
  },
  securityContainer: {
    padding: '0 1.875rem',
    [theme.breakpoints.down('sm')]: {
      padding: '0 1rem'
    }
  },
  divider: {
    height: '2px',
    background: theme.palette.whiteLilac,
    marginTop: '1.375rem',
    marginBottom: '2.75rem'
  },
  pillBtn: {
    fontSize: '0.875rem',
    backgroundColor: theme.palette.culture
  },
  pillContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  passwordContainer: {
    marginBottom: '3.625rem',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '1.5rem'
    }
  },
  questionContainer: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '2rem'
    }
  },
  selectQuestion: {
    width: '100% !important',
    padding: '16px 14px',
    border: '2px solid #EEF0F8',
    opacity: '1'
  }
}))

function PreferencesView () {
  const { authState: oktaAuthState } = useOktaAuth()
  const authState = useAuthState(oktaAuthState)
  const classes = useStyles()
  const theme = useTheme()

  const [user, setUser] = useState(null)
  const [alert, setAlert] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [question, setQuestion] = useState(null)
  const [updateQuestion, setUpdateQuestion] = useState(false)

  const [editMode, setIsEditMode] = useBoolean(false)
  const [openResetPass, setResetPass] = useBoolean(false)
  const [openResetQn, setResetQn] = useBoolean(false)
  const [loadingQn, setLoadingQn] = useBoolean(false)

  const history = useHistory()

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email
    }
  })

  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { isSubmitting, isValid, errors, isDirty }
  } = formMethods

  const onSubmitHandler = useCallback(async (values) => {
    setIsSaving(true)

    const userBody = {
      firstName: values.firstName,
      lastName: values.lastName,
      avatarMediaId: values.avatarMediaId || null
    }

    try {
      await updateMe(userBody)
      setAlert({ ...SUCCESS_ALERT })
    } catch {
      setAlert({ ...ERROR_ALERT })
    }
  }, [])

  const loadRecoveryQuestion = useCallback(async (id) => {
    setLoadingQn.on()

    try {
      const { data: { question: recoveryQuestion } } = await getRecoveryQuestion({
        id
      })

      setQuestion(recoveryQuestion)
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingQn.off()
    }
  }, [setLoadingQn])

  useEffect(() => {
    async function getUserData () {
      const { data } = await fetchUser(authState.accessToken.claims.uid)
      setUser(data)

      const { firstName, lastName, email } = data

      reset({
        firstName: firstName,
        lastName: lastName,
        email: email
      })

      loadRecoveryQuestion(authState.accessToken.claims.uid)
    }

    getUserData()
  }, [
    authState,
    reset,
    loadRecoveryQuestion
  ])

  useEffect(() => {
    if (updateQuestion) {
      loadRecoveryQuestion(authState.accessToken.claims.uid)
    }
  }, [updateQuestion, loadRecoveryQuestion, user, authState.accessToken.claims.uid])

  if (!user) {
    return (
      <PreferencesSkeleton />
    )
  }

  return (
    <>
      <Container fixed maxWidth={false} className={classes.container}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmitHandler)}>

            <Grid container spacing={2} className={classes.infoContainer}>
              <Grid container direction='column' alignItems='center' md={2} xs={12}>
                <ClientProfilePicture
                  disabled={!editMode}
                  disabledOpacity={1}
                  formMediaName='avatarMediaId'
                  defaultMediaId={user?.avatarMediaId}
                  uploadLabel={editMode ? 'Upload' : ''}
                />
                <Button
                  className={classes.editBtn}
                  onClick={setIsEditMode.toggle}
                  variant='outlined'
                >
                  {editMode ? 'Editing' : 'Edit'}
                </Button>
              </Grid>
              <Grid item md={10} xs={12}>
                <Grid container spacing={4}>
                  <Grid item md={6} xs={12}>
                    <Controller
                      name='firstName'
                      render={() => (
                        <TextInput
                          fullWidth
                          name='firstName'
                          placeholder='First Name'
                          label='First Name*'
                          error={errors.firstName}
                          {...register('firstName')}
                        />)}
                      control={control}
                      rules={{ required: true }}
                    />
                    {errors.firstName && (
                      <Text
                        text={messageToUse(errors.firstName)}
                        color={theme.palette.error.main}
                        className={classes.confirmError}
                      />
                    )}
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Controller
                      name='lastName'
                      render={() => (
                        <TextInput
                          fullWidth
                          name='lastName'
                          placeholder='Last Name'
                          label='Last Name*'
                          error={errors.lastName}
                          {...register('lastName')}
                        />)}
                      control={control}
                      rules={{ required: true }}
                    />
                    {errors.lastName && (
                      <Text
                        text={messageToUse(errors.lastName)}
                        color={theme.palette.error.main}
                        className={classes.confirmError}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      name='email'
                      placeholder='Email'
                      label='Email'
                      fullWidth
                      disabled
                      {...register('email')}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Text
                  text='Security Preferences'
                  color={theme.palette.romanSilver}
                  customFontSize='1rem'
                  textTransform='uppercase'
                  customFontWeight='900'
                  className={classes.securityPref}
                />
                <Divider className={classes.divider} />
              </Grid>
            </Grid>

            <Grid
              container
              className={classes.securityContainer}
            >
              <Grid container xs={12} spacing={2} className={classes.passwordContainer}>
                <Grid item md={4} xs={12}>
                  <Text
                    text='Password'
                    color={theme.palette.darkJungle}
                    customFontSize='1rem'
                    customFontWeight='900'
                  />
                  <Text color={theme.palette.dimGray} customFontSize='0.875rem'>
                    Change your password by clicking the <b>Reset Password</b> button.
                  </Text>
                </Grid>
                <Grid item md={4} xs={12}>
                  <TextInput
                    fullWidth
                    disabled
                    type='password'
                    name='password'
                    placeholder='•••••••••••••••••'
                  />
                </Grid>
                <Grid item md={4} xs={12} className={classes.pillContainer}>
                  <RoundedButton
                    onClick={setResetPass.on}
                    size={BUTTON_SIZES.medium}
                    className={classes.pillBtn}
                  >
                    Reset Password
                  </RoundedButton>
                </Grid>
              </Grid>

              <Grid container xs={12} spacing={2} className={classes.questionContainer}>
                <Grid item md={4} xs={12}>
                  <Text
                    text='Security Question'
                    color={theme.palette.darkJungle}
                    customFontSize='1rem'
                    customFontWeight='900'
                  />
                  <Text color={theme.palette.dimGray} customFontSize='0.875rem'>
                    Update your security question by clicking the <b>Change Question</b> button,
                    note your password will be required to make this change.
                  </Text>
                </Grid>
                <Grid item md={4} xs={12}>
                  <Select
                    placeholder={question}
                    variant='outlined'
                    size='medium'
                    className={classes.selectQuestion}
                    loadingOpts={loadingQn}
                    readOnly
                    disabled
                  />
                </Grid>
                <Grid item md={4} xs={12} className={classes.pillContainer}>
                  <RoundedButton
                    onClick={setResetQn.on}
                    size={BUTTON_SIZES.medium}
                    className={classes.pillBtn}
                  >
                    Change Question
                  </RoundedButton>
                </Grid>
              </Grid>
            </Grid>

            {
              isDirty && (
                <Grid container spacing={4} justifyContent='center' className={classes.bottomContainer}>
                  <Grid item md={3} lg={2} xs={12}>
                    <RoundedButton
                      fullWidth
                      onClick={() => history.push('/')}
                      size={BUTTON_SIZES.medium}
                    >
                      Cancel
                    </RoundedButton>
                  </Grid>
                  <Grid item md={3} lg={2} xs={12}>
                    <RoundedButton
                      primary
                      fullWidth
                      type='submit'
                      size={BUTTON_SIZES.medium}
                      isLoading={isSaving}
                      disabled={!isValid || isSubmitting}
                    >
                      Save Changes
                    </RoundedButton>
                  </Grid>
                </Grid>
              )
            }
          </form>
        </FormProvider>
        <SnackAlert alert={alert} />
      </Container>

      <ResetPasswordDialog
        username={user?.email}
        uid={authState?.accessToken?.claims?.uid}
        openedDialog={openResetPass}
        closeDialog={setResetPass.off}
      />

      <ResetRecoveryQnDialog
        username={user?.email}
        openedDialog={openResetQn}
        closeDialog={setResetQn.off}
        updateQuestion={setUpdateQuestion}
      />
    </>
  )
}

export default PreferencesView
